
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dispatch, FC, ReactElement, useEffect, useState, useMemo } from "react";
import { ConnectedProps, connect, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Container,
  IconButton,
  Toolbar,
  Typography,
  Divider,
  Box,
  Select,
  MenuItem,
  Avatar,
  SelectChangeEvent,
  FormControl,
} from "@mui/material";
import {
  Help, 
  Menu, 
  ReviewsRounded,
} from "@mui/icons-material";
import { useProSidebar } from "react-pro-sidebar";

import {ConfigDrawer} from "../utilities/ConfigDrawer";
import { 
  useColorModeContext,
  useReportsConfirmed,
  useSidebar, 
} from "../../hooks";
import { RootState } from "../../redux/store/store";
import { ActivateChangeUserPassword, LogOut, SetSelectedProjectPermissions } from "../../redux/actionCreators/authActions";
import { GetContracts, SetContractChange } from "../../redux/actionCreators/contractActions";
import { GetReportDates, SetCurrentReportDate } from "../../redux/actionCreators/reportDateActions";
import { IContract } from "../../models/contract";
import { IReportDate } from "../../models/reportDate";
import { IContractsState } from "../../redux/reducers/contractReducer";
import { IReportDatesState } from "../../redux/reducers/reportDateReducer";
import { LazyTooltip } from "../utilities/ToolTip";
import { IRequestContractChange } from "../../models/permission";
import { ColorModeContextType } from "../../context";
import { GetConfirmedReports } from "../../redux/actionCreators/reportConfirmActions";


const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 50,
    },
  },
};

const Navbar: FC<NavbarProps> = ({ 
    userid, 
    fullname,
    userImage,
    userContractPermissions,
    getContracts,
    getReportDates,
    setContractChange,
    setCurrentReportDate,
    activateChangeUserPassword,
    logout,
  }): ReactElement => {
  const { colorMode } = useColorModeContext() as ColorModeContextType;
  const navigate = useNavigate();
  const { broken } = useProSidebar();
  const { toggle } = useSidebar();
  const { retrieveConfirmedReports } = useReportsConfirmed()

  useEffect(() => {
    getContracts(userid!);
    getReportDates();
  }, [getContracts, getReportDates, userid])

  const contractState: IContractsState = useSelector((state: RootState) => state.contracts)
  const contracts: IContract[] = contractState.contracts
  const reportDateState: IReportDatesState = useSelector((state: RootState) => state.reportDates)
  const reportDates: IReportDate[] = reportDateState.reportDates
  
  const [contractId, setContractId] = useState<string>('');
  const [monthDateId, setMonthDateId] = useState<string>('');

  useEffect(() => {
    if(contracts !== null && contracts.length > 0){
      setContractId(String(contracts[0].contractid));
      const request: IRequestContractChange = {
        contractid: contracts[0].contractid,
        currency: contracts[0].currency,
        userContractPermissions: userContractPermissions
      }
      setContractChange(contracts[0].contractid, request);      

      if(!(userContractPermissions.length === 1 && userContractPermissions[0].projectid === null))
      {
        SetSelectedProjectPermissions(request)
      }
    }
  }, [contracts, userContractPermissions, setContractChange, ])
 
  useEffect(() => {
    if(contractId !== '' && monthDateId !== ''){
      retrieveConfirmedReports(contractId, monthDateId)
      // setConfirmedReports(result) 
    }
  }, [retrieveConfirmedReports, contractId, monthDateId])

  useEffect(() => {
    if(reportDates !== null && reportDates.length > 0){
      setMonthDateId(String(reportDates[0].dateid));
      setCurrentReportDate(reportDates[0].dateid);
      // console.log('^^^^dateid: ', String(reportDates[0].dateid))
    }
  }, [reportDates, setCurrentReportDate])

  const handleContractSelectChange = (event: SelectChangeEvent<typeof contractId>) => {
    setContractId(event.target.value);  
    const request: IRequestContractChange = {
      contractid: Number(event.target.value), 
      currency: contracts.filter(contract => contract.contractid === Number(event.target.value))[0].currency,
      userContractPermissions: userContractPermissions
    }
    // console.log('*** setCurrentContract request: ', request)
    setContractChange(Number(event.target.value), request);
    // console.log('condition: ', !(userContractPermissions.length === 1 && userContractPermissions[0].projectid === null))
    if(!(userContractPermissions.length === 1 && userContractPermissions[0].projectid === null))
    {
      // console.log('SetSelectedProjectPermissions ...')
      SetSelectedProjectPermissions(request)
    }
    // getConfirmedReports(event.target.value, monthDateId)
  }

  const handleMonthDateSelectChange = (event: SelectChangeEvent<typeof monthDateId>) => {
    setMonthDateId(event.target.value);
    setCurrentReportDate(Number(event.target.value));
    // getConfirmedReports(contractId, event.target.value)
  }

  const selectedContract = useMemo(() => {
    if (!contractId || contracts.length === 0) {
      return undefined;
    }
    return contracts.find(
      (contract) => String(contract.contractid) === String(contractId)
    );
  }, [contractId, contracts]);
  
  const isContractCompleted = Boolean(selectedContract?.iscompleted);


  const signOut = () => {
    logout();
    navigate('/', { replace: true})
  }

  return (
      <Container 
        maxWidth="xl" 
        sx={{
          backgroundColor: colorMode !== 'dark' ? 'rgb(222, 235, 243)' : '#0d1e31',
          borderBottom: '.4px solid rgb(214, 216, 216)',
        }}
      >
        <Toolbar 
          disableGutters 
          sx={{
            display:'flex', 
            justifyContent:'space-between'
          }}
        >
          <Box 
            sx={{
              display:'flex', 
              justifyContent:'flex-start'
            }}
          >
            {broken && (
            <IconButton
              onClick={toggle}
              sx={{
                color:'inherit',
                mt:-1.5}}
            >
              <Menu />
            </IconButton>)}
          </Box>
          <Box flex={3} display='flex' justifyContent='center'>
          <Typography variant="body2" sx={{ fontFamily:'B Nazanin'}}> نام پروژه: </Typography>
            <FormControl sx={{ mt:-1, mx:1, px:1, minWidth: 350}} size="small">
              {/* <InputLabel id="demo-select-small"></InputLabel> */}
              <Select 
                labelId="demo-select-small"
                id="demo-select-small"
                value={contractId}
                label=""
                onChange={handleContractSelectChange}
                MenuProps={MenuProps}
                sx={{
                  fontFamily:'B Nazanin', 
                  fontSize:14, 
                  fontWeight:600, 
                  borderRadius:2,
                  // border: '.4px solid rgb(196, 199, 199)',
                  // '& .MuiOutlinedInput-notchedOutline': {
                  //   borderColor: isContractCompleted ? 'red' : 'rgb(196, 199, 199)',
                  //   borderWidth: isContractCompleted ? 1 : 0.4,
                  // },
                  // '&:hover .MuiOutlinedInput-notchedOutline': {
                  //   borderColor: isContractCompleted ? 'red' : 'rgb(196, 199, 199)',
                  // },
                  // '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  //   borderColor: isContractCompleted ? 'red' : 'rgb(196, 199, 199)',
                  // },
                }}
              >
                  {contracts.map((contract: IContract) => 
                  {
                    return(
                      <MenuItem
                      key={contract.contractid}
                      value={contract.contractid}
                      sx={{
                        fontFamily:'B Nazanin', 
                        fontSize:14, 
                        fontWeight:600, 
                        color: contract.iscompleted ? 'red' : 'black',
                        direction:'rtl'
                      }}
                    >
                      {contract.contract}
                    </MenuItem>
                    );
                  })}
              </Select>
            </FormControl> 
            <Typography variant="body2" sx={{ fontFamily:'B Nazanin'}}>تاریخ منتهی به:</Typography>
            <FormControl sx={{ mt:-1, mx:1, px:.2, minWidth: 90 }} size="small">
              {/* <InputLabel id="demo-select-small"></InputLabel> */}
              <Select
                labelId="demo-select-small"
                id="demo-select-small"
                value={monthDateId}
                label=""
                onChange={handleMonthDateSelectChange}
                MenuProps={MenuProps}
                sx={{fontFamily:'B Nazanin', border:'.4px solid rgb(196, 199, 199)', borderRadius:2}}
              >
                  {reportDates.map((reportDate: IReportDate) => 
                  {
                    return(
                      <MenuItem
                        key={reportDate.dateid}
                        value={reportDate.dateid}
                        sx={{fontFamily:'B Nazanin'}}
                        >
                        {reportDate.shamsiDate}
                      </MenuItem>
                    );
                    })}
              </Select>
            </FormControl>
            <Box display='flex' justifyContent='end'>
              <Box>
                <Typography fontFamily='B Nazanin' fontWeight={600} color='red'>
                  {isContractCompleted ? 'این پروژه به اتمام رسیده است' : ''}
                </Typography>
              </Box>
            </Box>         
          </Box>
          <Box display='flex'>
            <Box sx={{mt:-1, mx:1.5, display: { xs:"none", sm:'flex' }}}>
              <LazyTooltip 
                title={"بازدید ها"}
              >
                <ReviewsRounded 
                  onClick={() => {
                    navigate('/report/siteVisitReport', { replace: true });
                  }}
                  sx={{
                    m:'auto', 
                    color:'inherit',
                    width:20, 
                    Width:20
                  }} 
                />
              </LazyTooltip>
              <Box sx={{ display: { xs:"none", sm:'flex' }, py:0, my:0, mx:1}}> 
                  <LazyTooltip 
                    title={"راهنما"}
                  >
                    <Help 
                      sx={{
                        m:'auto', 
                        color:'inherit',
                        width:20, 
                        height:20
                      }} 
                    />
                  </LazyTooltip>
              </Box>
              <Divider orientation="vertical" flexItem sx={{py:2, mt:2.5, height:'40%'}}/>
              <Select id='navbarSelect'
                sx={{
                  '.MuiOutlinedInput-notchedOutline': { border: 0 }, 
                  maxHeight:'100%', 
                  px:0, 
                  mx:0,
                  py:0, 
                  my:0 
                }}
                displayEmpty
                renderValue={(value) => {
                  return (
                    <Box sx={{ display:"flex", justifyContent:'flex-end', py:0, my:0 }}>
                      <>
                        <Typography 
                          sx={{display: { xs: "none", lg: "flex"}, fontFamily:'B Nazanin'}} 
                          m='auto' 
                          color='text.secondary'
                          variant='button'
                        >
                            {fullname} &nbsp;
                        </Typography>
                        <Avatar 
                          sx={{ 
                            display: { xs: "none", md: "flex" }, 
                            width:45, 
                            height:45, 
                            m:0, 
                            mt:-.4,
                            p:0,
                            gap:.5
                          }}
                          // src='https://avatars.githubusercontent.com/u/47317870?s=400&u=79da86747deb409779c3575c0da73d90ad65fe81&v=4'
                          src={userImage}
                          alt='avatar'
                        />
                        {value}
                      </>
                    </Box>
                  );
                }}
              >
                <MenuItem 
                  onClick={() => {
                      activateChangeUserPassword(true);
                      navigate('auth/changepassword', { replace: true });
                  }}
                >
                  <Typography variant="button" fontFamily='B Nazanin'>
                    تغییر کلمه عبور
                  </Typography>
                </MenuItem>
                <MenuItem onClick={signOut}>
                  <Typography variant="button" fontFamily='B Nazanin'>
                    خروج
                  </Typography>
                </MenuItem>
              </Select>
            </Box>
            <Divider orientation="vertical" flexItem sx={{py:2, mt:1.65, height:'80%'}}/>
            <ConfigDrawer/>
          </Box>
        </Toolbar>
      </Container>
  );
};

const mapStateToProps = (state: RootState) => ({
  userid: state.auth.user?.id,
  permissions: state.auth.permissions,    
  fullname: state.auth.user?.full_name,
  userImage: state.auth.user?.user_img,
  userContractPermissions: state.auth.userContractPermissions,
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
getContracts: (userId: number) => 
  dispatch(GetContracts(userId)),
getReportDates: () => 
  dispatch(GetReportDates()),
getConfirmedReports: (currentContractId: string, currentReportDateId: string) => 
  dispatch(GetConfirmedReports(currentContractId, currentReportDateId)),
setContractChange: (contractid: number, request: IRequestContractChange) => 
  dispatch(SetContractChange(contractid, request)),
setCurrentReportDate: (currentReportDateId: number) => 
  dispatch(SetCurrentReportDate(currentReportDateId)),
activateChangeUserPassword: (active: boolean) => 
  dispatch(ActivateChangeUserPassword(active)),
  logout: () => dispatch(LogOut()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type NavbarProps = ConnectedProps<typeof connector>;

export default connector(Navbar)

